import { useCallback, useContext, useEffect, useState } from 'react';
import '../App.css';
import DatatableSortButton from '../components/datatables/datatableSortButton';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "../components/ui/accordion";
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';

import { DotsHorizontalIcon, ExclamationTriangleIcon, Pencil1Icon } from '@radix-ui/react-icons';
import { Link, useNavigate, useParams } from 'react-router';
import CM_Logo from '../CM_Digital_Logo.webp';
import BunnyChildren from '../components/bunnyChildren';
import BunnyInfoItem from '../components/bunnyInfoItem';
import BunnyQualifications from '../components/bunnyQualifications';
import { BunnyCupsResultsDatatable } from '../components/datatables/BunnyCupsResultsDatatable';
import { HLBunnyCompetitionResults } from '../components/datatables/HLBunnyCompetitionResults';
import { LoanedToDatatable } from '../components/datatables/loanedToDatatable';
import ChangeBunnyOwnerDialog from '../components/dialogs/changeBunnyOwnerDialog';
import MarkBunnyDeceasedDialog from '../components/dialogs/markBunnyDeceasedDialog';
import FamilyTree from '../components/familyTree';
import ScrollToTop from '../components/scrollToTop';
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { Badge } from '../components/ui/badge';
import { Dialog } from '../components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from '../components/ui/use-toast';
import { AuthContext } from '../context/AuthContext';
import sport_classes from '../enums/sport_classes.json';
import api from '../lib/api';

export const columns = [
    {
        accessorKey: "email",
        header: ({ column }) => <DatatableSortButton column={column}>E-post</DatatableSortButton>,
    },
    {
        accessorKey: "created_at",
        header: ({ column }) => <DatatableSortButton column={column}>Tidspunkt för utlåning</DatatableSortButton>,

        cell: info => {
            const date = new Date(info.row.original.created_at);
            return date.toLocaleDateString(undefined, {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
            });
        }
    },
    {
        accessorKey: "description",
        header: 'Beskrivning',
    },
];

function BunnySingle() {
    const [isLoading, setIsLoading] = useState(true);
    const { auth } = useContext(AuthContext);

    const navigate = useNavigate();
    const [bunny, setBunny] = useState();
    let { bunnyId } = useParams();

    const fetchBunnyInformation = useCallback(() => {
        api.get(`https://skhrf-api.cmdigital.se/v1/bunnies/?bunnyId=${bunnyId}`)
            .then(response => {

                setBunny(response.data);
                setIsLoading(false);
            })
            .catch(error => {
                console.error('Error fetching bunny:', error);
                setIsLoading(false);

                // Redirect to /bunnies if 404 error
                if (error.response?.status === 404) {
                    toast({
                        variant: 'destructive',
                        title: 'Något gick fel.',
                        description: 'Kaninen du efterfrågade finns inte.',
                    });
                    navigate('/bunnies');
                }
            });
    }, [bunnyId, navigate, toast]);

    useEffect(() => {
        fetchBunnyInformation();
    }, [fetchBunnyInformation]);

    const canEditBunny = !isLoading && (auth.user?.superadmin || auth.user?.bunnyadmin || auth.user?.id === bunny?.owner);

    const genderInSwedish = (gender) => {
        if (gender === "male") return "Hane";
        if (gender === "female") return "Hona";
        return gender; // fallback in case there are other values
    };

    const getSportClassNameById = (id) => {
        const sportClass = sport_classes.find((sport_class) => sport_class.id === id);
        if (sportClass) {
            // Remove "Rak", "Krok", "Höjd", or "Längd" from the start of the class name
            return sportClass.sport_class_name.replace(/^(Rak|Krok|Längd|Höjd)\s*/, '');
        }
        return "Okänd klass"; // Fallback if ID is not found
    };

    const titleMappings = {
        "Ch": "Champion",
        "Gt Ch": "Great Champion",
        "S Ch": "Super Champion",
        "Gd Ch": "Grand Champion",
    };

    const getFullTitle = (shortTitle) => titleMappings[shortTitle] || 'Ingen titel';

    return (
        <div className='flex w-full items-center justify-center'>
            <ScrollToTop />
            <div className='flex w-5/6 sm:w-3/4 flex-col py-6 gap-3'>
                <div className='grid grid-cols-1 xl:grid-cols-2 items-center'>
                    <div className='flex flex-col sm:flex-row gap-1'>
                        <h1 className='font-medium text-2xl'>
                            {isLoading ? (
                                <Skeleton className="h-10 w-52" />
                            ) : (
                                <>
                                    {bunny?.full_name}
                                </>
                            )}
                        </h1>
                        {bunny?.death_date && (
                            <Badge variant="destructive" className="sm:ml-3 w-fit">
                                Avliden
                            </Badge>
                        )}
                    </div>
                    {canEditBunny && (
                        <div className='grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 pt-3'>
                            <Dialog modal={true}>
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild className='col-span-2 xl:col-start-3 xl:col-span-2'>
                                        <Button variant='secondary'><DotsHorizontalIcon className='mr-3' />Verktyg</Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent className='dropdown-content-width-full'>
                                        <DropdownMenuLabel>Verktyg</DropdownMenuLabel>
                                        <DropdownMenuSeparator />
                                        {(auth?.user?.superadmin || auth?.user?.bunnyadmin) &&
                                            <DropdownMenuItem className='cursor-pointer' onClick={() => navigate(`/bunny/${bunny?.id}/edit`)}>
                                                <>
                                                    <Pencil1Icon className='mr-3' />
                                                    Ändra kanininformation
                                                </>
                                            </DropdownMenuItem>
                                        }
                                        <ChangeBunnyOwnerDialog bunny={bunny} fetchBunny={fetchBunnyInformation} dropdown={true} />
                                        {!bunny?.death_date &&
                                            <MarkBunnyDeceasedDialog bunny={bunny} fetchBunnies={fetchBunnyInformation} />
                                        }
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </Dialog>
                        </div>
                    )}
                </div>
                <div>
                    {!isLoading && bunny?.death_date && (
                        <BunnyInfoItem
                            label="Dödsdatum"
                            value={new Date(bunny?.death_date).toLocaleDateString(undefined, {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                            })}
                            isLoading={isLoading}
                        />
                    )}
                </div>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
                    <div>
                        <div className='grid gap-2'>
                            <BunnyInfoItem
                                label="Födelsedatum"
                                value={new Date(bunny?.birth_date).toLocaleDateString(undefined, {
                                    year: 'numeric',
                                    month: '2-digit',
                                    day: '2-digit',
                                })}
                                isLoading={isLoading}
                            />
                            <BunnyInfoItem
                                label="Färg"
                                value={bunny?.color === '056cf2' ? <img src={CM_Logo} className='h-6' alt='CM Digital AB logo'></img> : bunny?.color}
                                isLoading={isLoading}
                                styling={bunny?.color === '056cf2' ? 'text-[#056cf2]' : undefined}
                            />
                            <BunnyInfoItem
                                label="Kön"
                                value={genderInSwedish(bunny?.gender)}
                                isLoading={isLoading}
                            />
                            <BunnyInfoItem
                                label="Huvudförare"
                                value={bunny?.owner_full_name}
                                isLoading={isLoading}
                            />
                            <BunnyInfoItem
                                label="Registreringsnummer"
                                value={bunny?.registration_number ? bunny?.registration_number : 'Ej registrerad'}
                                isLoading={isLoading}
                            />
                        </div>
                    </div>
                    <div>
                        <div className='grid gap-2'>
                            <BunnyInfoItem
                                label="Rak"
                                value={getSportClassNameById(bunny?.class_straight)}
                                isLoading={isLoading}
                            />
                            <BunnyInfoItem
                                label="Krok"
                                value={getSportClassNameById(bunny?.class_crooked)}
                                isLoading={isLoading}
                            />
                            <BunnyInfoItem
                                label="Höjd"
                                value={getSportClassNameById(bunny?.class_height)}
                                isLoading={isLoading}
                            />
                            <BunnyInfoItem
                                label="Längd"
                                value={getSportClassNameById(bunny?.class_length)}
                                isLoading={isLoading}
                            />
                            <BunnyInfoItem
                                label="Titel"
                                value={getFullTitle(bunny?.title)}
                                isLoading={isLoading}
                            />
                        </div>
                    </div>

                </div>
                {bunny?.breeding_information && bunny.breeding_information.length > 0 && (
                    // <BunnyInfoItem
                    //             label="Avelsinformation"
                    //             value={bunny?.breeding_information}
                    //             isLoading={isLoading}
                    //             styling={'border bg-yellow-100 border-yellow-300'}
                    //         />
                    <Alert className='[&>svg]:text-yellow-600  bg-yellow-50/60 border-yellow-300' isLoading={isLoading}>
                        <ExclamationTriangleIcon />
                        <AlertTitle>Avelsinformation</AlertTitle>
                        <AlertDescription>
                            {bunny?.breeding_information}
                        </AlertDescription>
                    </Alert>
                )}
                <Accordion type="single" collapsible>
                    {(!isLoading && bunny?.registration_number !== null) && (
                        <AccordionItem value='item-1'>
                            <AccordionTrigger>Kvalifikationer</AccordionTrigger>
                            <AccordionContent>
                                <BunnyQualifications bunny={bunny} fetchBunnyInformation={fetchBunnyInformation} />
                            </AccordionContent>
                        </AccordionItem>
                    )}
                    {(!isLoading && bunny?.registration_number !== null) && (
                        <AccordionItem value='item-2'>
                            <AccordionTrigger>Resultat</AccordionTrigger>
                            <AccordionContent className='flex flex-col gap-3'>
                                <div className='rounded-md bg-gray-100 p-3'>
                                    <h2 className='font-medium text-lg mb-3'>Tävlingar</h2>
                                    <HLBunnyCompetitionResults bunnyId={bunny?.id} />
                                </div>
                                <div className='rounded-md bg-gray-100 p-3'>
                                    <h2 className='font-medium text-lg mb-3'>Cuper</h2>
                                    <BunnyCupsResultsDatatable bunnyId={bunny?.id} />
                                </div>
                            </AccordionContent>
                        </AccordionItem>
                    )}
                    {!isLoading && (auth?.user?.id === bunny?.owner || auth?.user?.superadmin || auth?.user?.bunnyadmin) && bunny?.approved_at !== null && (
                        <AccordionItem value='item-3'>
                            <AccordionTrigger>Förare och utlåningar</AccordionTrigger>
                            <AccordionContent>
                                {isLoading ? (
                                    <Skeleton className='w-full h-32' />
                                ) : (
                                    <LoanedToDatatable bunny={bunny} />
                                )}
                            </AccordionContent>
                        </AccordionItem>
                    )}
                    <AccordionItem value='item-4'>
                        <AccordionTrigger>Släktträd</AccordionTrigger>
                        <AccordionContent>
                            <FamilyTree bunny={bunny} />
                        </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value='item-5'>
                        <AccordionTrigger>Avkommor</AccordionTrigger>
                        <AccordionContent>
                            <BunnyChildren bunny={bunny} />
                        </AccordionContent>
                    </AccordionItem>
                </Accordion>
            </div>
        </div>
    );
}

export default BunnySingle;
